import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gamepad2, Sparkles, GraduationCap, Presentation, BarChart3, ShieldCheck,
  LogIn, Globe, ArrowRight, Check, Users, School, Trophy, BookOpen, Phone, Mail, MessageCircle,
} from "lucide-react";
import { useLang } from "../i18n";
import { api } from "../api";
import { ASSETS } from "../assets";

const COPY = {
  ar: {
    nav: ["المزايا", "كيف يعمل", "الأدوار", "الأسئلة"],
    login: "تسجيل الدخول",
    demo: "اطلب تجربة لمدرستك",
    heroKicker: "منصة تعليم الإنجليزية للمدارس",
    heroTitle: "إنجلش لاب — إنجليزية يحبها الطلاب، وإدارة يثق بها المعلم",
    heroText:
      "منصة واحدة تجمع الدروس التفاعلية والألعاب التعليمية والذكاء الاصطناعي ولوحة تدريس للمعلم وتقارير فورية — كل مدرسة بمساحتها الخاصة وكل صف بمحتواه المناسب.",
    stats: [["٤", "أدوار متكاملة"], ["٧", "صفوف دراسية"], ["١٠٠٪", "عربي + إنجليزي"]],
    featuresTitle: "لماذا إنجلش لاب؟",
    features: [
      ["تعلّم مُلعّب", "ألعاب كلمات ونطق وذاكرة ونقاط وشارات ولوحة صدارة تحفّز الطالب على العودة كل يوم.", Gamepad2],
      ["ذكاء اصطناعي مساعد", "كويزات تُبنى فوراً، معلّم يجيب الطالب بلغته، وأوراق عمل جاهزة للمعلّم بضغطة واحدة.", Sparkles],
      ["محتوى لكل صف", "المالك يبني صفحة كل درس بالنص والصور والفيديو ويرتّبها كما يريد، والطالب يرى ما يناسب صفّه فقط.", GraduationCap],
      ["وضع تدريس للمعلّم", "عرض الدرس على الشاشة أو اللوح الذكي بشرائح واضحة تعمل على الجوال والتابلت.", Presentation],
      ["تقارير فورية", "تقدّم الطالب ونتائجه ونشاطه أمام المعلّم وإدارة المدرسة في لوحة واحدة.", BarChart3],
      ["خصوصية وعزل كامل", "بيانات كل مدرسة معزولة تماماً، والحسابات تُدار بصلاحيات واضحة لكل دور.", ShieldCheck],
    ],
    howTitle: "كيف يعمل؟",
    how: [
      ["اطلب تجربة", "أرسل بيانات مدرستك ونجهّز لك مساحة خاصة بشعار مدرستك وألوانها."],
      ["أنشئ الحسابات", "إدارة المدرسة تضيف المعلمين والطلاب والصفوف في دقائق."],
      ["ابدأ التعلّم", "الطالب يلعب ويتعلّم، والمعلّم يدرّس ويتابع النتائج مباشرة."],
    ],
    rolesTitle: "لكل دور لوحته",
    roles: [
      ["الطالب", "دروس، ألعاب، كويز، معلّم ذكي، قاموس، نقاط ومسابقات.", Trophy],
      ["المعلّم", "وضع تدريس، مهام، أوراق عمل، متابعة صفوفه.", BookOpen],
      ["إدارة المدرسة", "حسابات المعلمين والطلاب، الصفوف، تقارير المدرسة.", School],
      ["مالك المنصة", "المدارس، المناهج، الفيديوهات، المسابقات، والهوية البصرية.", Users],
    ],
    formTitle: "اطلب تجربة لمدرستك",
    formText: "أرسل بياناتك وسنتواصل معك لتفعيل مساحة تجربة لمدرستك.",
    fields: {
      school_name: "اسم المدرسة", contact_name: "اسم المسؤول", email: "البريد الإلكتروني",
      phone: "رقم الجوال", city: "المدينة", students_count: "عدد الطلاب تقريباً",
      note: "ملاحظات (اختياري)",
    },
    send: "إرسال الطلب",
    sending: "جارٍ الإرسال...",
    sent: "تم إرسال طلبك! سنتواصل معك قريباً.",
    faqTitle: "أسئلة شائعة",
    faq: [
      ["هل المنصة تعمل على الجوال والتابلت؟", "نعم، الواجهة كاملة متجاوبة وتعمل على الجوال والتابلت والحاسوب واللوح الذكي."],
      ["هل الطالب يرى محتوى صفوف أخرى؟", "لا. المحتوى معزول بصرامة حسب صف الطالب ومدرسته."],
      ["من ينشئ حسابات الطلاب؟", "إدارة المدرسة تنشئها، أو تُستورد دفعة واحدة عبر ملف CSV."],
      ["هل يمكن تخصيص شعار المدرسة وألوانها؟", "نعم، لكل مدرسة شعارها وألوانها داخل المنصة."],
    ],
    footer: "منصة تعليم الإنجليزية للمدارس",
  },
  en: {
    nav: ["Features", "How it works", "Roles", "FAQ"],
    login: "Sign in",
    demo: "Request a school trial",
    heroKicker: "English learning platform for schools",
    heroTitle: "English Lab — English pupils love, control teachers trust",
    heroText:
      "Interactive lessons, learning games, built-in AI, a teaching mode for the classroom and instant reports — every school in its own space, every grade with its own content.",
    stats: [["4", "connected roles"], ["7", "school grades"], ["100%", "Arabic + English"]],
    featuresTitle: "Why English Lab?",
    features: [
      ["Gamified learning", "Word, listening and memory games with points, badges and leaderboards that bring pupils back daily.", Gamepad2],
      ["AI that helps", "Instant quizzes, a tutor that answers in the pupil's language, and one-click worksheets for teachers.", Sparkles],
      ["Content per grade", "The owner builds each lesson page with text, images and video in any order; pupils only see their own grade.", GraduationCap],
      ["Teaching mode", "Present any lesson as clean slides on a screen, tablet or smart board.", Presentation],
      ["Instant reports", "Progress, scores and activity for teachers and school management in one dashboard.", BarChart3],
      ["Private by design", "Each school's data is fully isolated with clear role-based permissions.", ShieldCheck],
    ],
    howTitle: "How it works",
    how: [
      ["Request a trial", "Send your school details and we prepare a space with your logo and colours."],
      ["Create accounts", "School management adds teachers, pupils and classes in minutes."],
      ["Start learning", "Pupils play and learn while teachers teach and track results live."],
    ],
    rolesTitle: "A dashboard for every role",
    roles: [
      ["Pupil", "Lessons, games, quizzes, AI tutor, dictionary, points and contests.", Trophy],
      ["Teacher", "Teaching mode, tasks, worksheets and class tracking.", BookOpen],
      ["School admin", "Teacher and pupil accounts, classes and school reports.", School],
      ["Platform owner", "Schools, curriculum, videos, contests and branding.", Users],
    ],
    formTitle: "Request a school trial",
    formText: "Send your details and we'll get in touch to activate a trial space.",
    fields: {
      school_name: "School name", contact_name: "Contact name", email: "Email",
      phone: "Phone", city: "City", students_count: "Approx. pupils", note: "Notes (optional)",
    },
    send: "Send request",
    sending: "Sending...",
    sent: "Request sent! We'll contact you soon.",
    faqTitle: "FAQ",
    faq: [
      ["Does it work on phones and tablets?", "Yes, the whole interface is responsive across phone, tablet, desktop and smart boards."],
      ["Can a pupil see other grades?", "No. Content is strictly isolated by the pupil's grade and school."],
      ["Who creates pupil accounts?", "School management creates them, or imports them in bulk via CSV."],
      ["Can we use our own logo and colours?", "Yes, every school has its own logo and colour theme."],
    ],
    footer: "English learning platform for schools",
  },
};

const EMPTY = { school_name: "", contact_name: "", email: "", phone: "", city: "", students_count: "", note: "" };

export const CONTACT = { phone: "0545073008", email: "Englishlab412@gmail.com", whatsapp: "972545073008" };

export default function Landing() {
  const { lang, setLang } = useLang();
  const c = COPY[lang] || COPY.ar;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      setForm(EMPTY);
      toast.success(c.sent);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر إرسال الطلب");
    } finally { setBusy(false); }
  };

  const waLink = () => {
    const f = form;
    const lines = lang === "ar"
      ? [`السلام عليكم، أرغب بتجربة إنجلش لاب لمدرستي.`,
         f.school_name && `المدرسة: ${f.school_name}`,
         f.contact_name && `المسؤول: ${f.contact_name}`,
         f.email && `البريد: ${f.email}`,
         f.phone && `الجوال: ${f.phone}`,
         f.city && `المدينة: ${f.city}`,
         f.students_count && `عدد الطلاب: ${f.students_count}`,
         f.note && `ملاحظات: ${f.note}`]
      : [`Hello, I'd like a trial of English Lab for my school.`,
         f.school_name && `School: ${f.school_name}`,
         f.contact_name && `Contact: ${f.contact_name}`,
         f.email && `Email: ${f.email}`,
         f.phone && `Phone: ${f.phone}`,
         f.city && `City: ${f.city}`,
         f.students_count && `Pupils: ${f.students_count}`,
         f.note && `Notes: ${f.note}`];
    return `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(lines.filter(Boolean).join("\n"))}`;
  };

  const field = (k, type = "text", required = true) => (
    <label className="block">
      <span className="text-xs font-bold text-white/60">{c.fields[k]}</span>
      <input className="input mt-1" type={type} required={required} value={form[k]}
        data-testid={`lead-${k.replaceAll("_", "-")}`}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </label>
  );

  return (
    <div className="app-bg min-h-screen text-white" data-testid="landing-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#071229]/70 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center gap-4">
          <img src={ASSETS.logo} alt="English Lab" className="h-10 w-10 rounded-xl object-cover" />
          <span className="font-black hidden sm:block">{lang === "ar" ? "إنجلش لاب" : "English Lab"}</span>
          <nav className="hidden lg:flex items-center gap-5 mx-4 text-sm text-white/70">
            {["features", "how", "roles", "faq"].map((id, i) => (
              <a key={id} href={`#${id}`} className="hover:text-white transition-colors">{c.nav[i]}</a>
            ))}
          </nav>
          <a href="#trial" data-testid="landing-cta-mobile"
            className="lg:hidden pro-btn bg-white/12 text-white border border-white/20 text-xs">{c.demo}</a>          <div className="ms-auto flex items-center gap-2">
            <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} data-testid="landing-lang-toggle"
              className="pro-btn bg-white/12 text-white border border-white/20 text-xs flex items-center gap-1">
              <Globe size={14} />{lang === "ar" ? "EN" : "AR"}</button>
            <a href="#trial" data-testid="landing-cta-top"
              className="pro-btn text-[#05122e] text-xs hidden sm:inline-flex"
              style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)" }}>{c.demo}</a>
            <Link to="/login" data-testid="landing-login-btn"
              className="pro-btn text-white text-xs inline-flex items-center gap-1"
              style={{ background: "linear-gradient(180deg,#3fa9ff,#1e63c8)" }}>
              <LogIn size={14} />{c.login}</Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="max-w-6xl mx-auto px-5 pt-14 pb-8 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-block text-xs font-bold tracking-wider px-3 py-1 rounded-full bg-white/10 border border-white/15">
            {c.heroKicker}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mt-5 glow-text">
            {c.heroTitle}</h1>
          <p className="text-base text-white/75 leading-loose mt-5 max-w-xl">{c.heroText}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <a href="#trial" data-testid="landing-cta-hero"
              className="pro-btn text-[#05122e] inline-flex items-center gap-2"
              style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)" }}>
              {c.demo}<ArrowRight size={16} /></a>
            <Link to="/login" className="pro-btn bg-white/12 text-white border border-white/20">{c.login}</Link>
          </div>
          <div className="flex gap-8 mt-10">
            {c.stats.map(([n, l]) => (
              <div key={l}>
                <div className="text-3xl font-black" style={{ color: "#7fd4ff" }}>{n}</div>
                <div className="text-xs text-white/70 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }} className="relative">
          <img src={ASSETS.mascot} alt="" className="w-full max-w-[26rem] mx-auto rounded-[32px] soft-mask mix-blend-screen float-slow" />
          <div className="glass-soft absolute bottom-0 start-2 p-3 text-xs font-bold flex items-center gap-2">
            <Check size={14} className="text-emerald-300" />{lang === "ar" ? "عربي / إنجليزي" : "Arabic / English"}
          </div>
        </motion.div>
      </section>

      {/* features */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="text-2xl md:text-3xl font-black">{c.featuresTitle}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {c.features.map(([title, text, Icon], i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="glass p-6 hover:-translate-y-1 transition-transform" data-testid={`feature-${i}`}>
              <div className="h-11 w-11 rounded-2xl grid place-items-center mb-4"
                style={{ background: "rgba(63,169,255,.18)" }}><Icon size={20} /></div>
              <h3 className="text-base md:text-lg font-black">{title}</h3>
              <p className="text-sm text-white/70 leading-loose mt-2">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* how */}
      <section id="how" className="max-w-6xl mx-auto px-5 py-10">
        <h2 className="text-2xl md:text-3xl font-black">{c.howTitle}</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {c.how.map(([title, text], i) => (
            <div key={title} className="glass p-6" data-testid={`step-${i}`}>
              <div className="text-4xl font-black" style={{ color: "#ffb43f" }}>{i + 1}</div>
              <h3 className="text-base md:text-lg font-black mt-2">{title}</h3>
              <p className="text-sm text-white/70 leading-loose mt-2">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* roles */}
      <section id="roles" className="max-w-6xl mx-auto px-5 py-10">
        <h2 className="text-2xl md:text-3xl font-black">{c.rolesTitle}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {c.roles.map(([title, text, Icon], i) => (
            <div key={title} className="glass-soft p-5" data-testid={`role-${i}`}>
              <Icon size={22} className="mb-3" style={{ color: "#7fd4ff" }} />
              <h3 className="text-base md:text-lg font-black">{title}</h3>
              <p className="text-sm text-white/70 leading-loose mt-2">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* trial form */}
      <section id="trial" className="max-w-6xl mx-auto px-5 py-16">
        <div className="glass p-6 md:p-10 grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-black">{c.formTitle}</h2>
            <p className="text-sm text-white/70 leading-loose mt-3">{c.formText}</p>
            <div className="mt-5 space-y-2" data-testid="contact-info">
              <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone"
                className="glass-soft px-4 py-3 flex items-center gap-3 text-sm font-bold hover:text-white transition-colors">
                <Phone size={16} style={{ color: "#ffb43f" }} />
                <span dir="ltr">{CONTACT.phone}</span></a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email"
                className="glass-soft px-4 py-3 flex items-center gap-3 text-sm font-bold hover:text-white transition-colors">
                <Mail size={16} style={{ color: "#7fd4ff" }} />
                <span dir="ltr">{CONTACT.email}</span></a>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
                className="px-4 py-3 rounded-2xl flex items-center gap-3 text-sm font-black text-white
                  hover:brightness-110 transition-all"
                style={{ background: "linear-gradient(180deg,#3ad07a,#189d52)" }}>
                <MessageCircle size={16} />
                {lang === "ar" ? "تواصل عبر واتساب" : "Chat on WhatsApp"}</a>
            </div>
            <img src={ASSETS.island} alt="" className="w-full rounded-3xl mt-6 hidden lg:block soft-mask-wide mix-blend-screen" />
          </div>
          {done ? (
            <div className="glass-soft p-8 text-center" data-testid="lead-success">
              <Check size={34} className="mx-auto text-emerald-300" />
              <p className="font-black mt-3">{c.sent}</p>
              <button onClick={() => setDone(false)} data-testid="lead-again"
                className="pro-btn bg-white/12 text-white border border-white/20 text-xs mt-5">
                {lang === "ar" ? "إرسال طلب آخر" : "Send another"}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="lead-form">
              <div className="grid sm:grid-cols-2 gap-3">
                {field("school_name")}
                {field("contact_name")}
                {field("email", "email")}
                {field("phone", "tel")}
                {field("city", "text", false)}
                {field("students_count", "text", false)}
              </div>
              <label className="block">
                <span className="text-xs font-bold text-white/60">{c.fields.note}</span>
                <textarea className="input mt-1" rows={3} value={form.note} data-testid="lead-note"
                  onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </label>
              <button disabled={busy} data-testid="lead-submit"
                className="pro-btn text-[#05122e] w-full"
                style={{ background: "linear-gradient(180deg,#ffb43f,#ef8a00)" }}>
                {busy ? c.sending : c.send}</button>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="lead-whatsapp"
                className="pro-btn w-full text-white flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(180deg,#3ad07a,#189d52)" }}>
                <MessageCircle size={16} />
                {lang === "ar" ? "أرسل الطلب عبر واتساب" : "Send via WhatsApp"}</a>
            </form>
          )}
        </div>
      </section>

      {/* faq */}
      <section id="faq" className="max-w-6xl mx-auto px-5 py-10">
        <h2 className="text-2xl md:text-3xl font-black">{c.faqTitle}</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          {c.faq.map(([q, a], i) => (
            <details key={q} className="glass-soft p-5" data-testid={`faq-${i}`}>
              <summary className="font-bold cursor-pointer">{q}</summary>
              <p className="text-sm text-white/70 leading-loose mt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/10 mt-10">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-wrap items-center gap-4 text-sm text-white/70">
          <img src={ASSETS.logo} alt="" className="h-9 w-9 rounded-xl object-cover" />
          <span>{lang === "ar" ? "إنجلش لاب" : "English Lab"} — {c.footer}</span>
          <a href={`tel:${CONTACT.phone}`} dir="ltr" data-testid="footer-phone"
            className="hover:text-white transition-colors">{CONTACT.phone}</a>
          <a href={`mailto:${CONTACT.email}`} dir="ltr" data-testid="footer-email"
            className="hover:text-white transition-colors">{CONTACT.email}</a>
          <Link to="/login" className="ms-auto hover:text-white transition-colors">{c.login}</Link>
        </div>
      </footer>

      <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer"
        data-testid="whatsapp-fab" aria-label="WhatsApp"
        className="fixed bottom-5 end-5 z-40 h-14 w-14 rounded-full grid place-items-center text-white
          shadow-2xl hover:scale-110 transition-transform"
        style={{ background: "linear-gradient(180deg,#3ad07a,#189d52)" }}>
        <MessageCircle size={26} />
      </a>
    </div>
  );
}
